/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetCatalogTreeParams,
  ListCatalogTreeCatalogsParams,
  ListCatalogTreeSchemasParams,
  ListCatalogTreeSchemasPathParameters,
  ListCatalogTreeTablesParams,
  ListCatalogTreeTablesPathParameters,
  TLCatalogTreeCatalogPageResponse,
  TLCatalogTreeResponse,
  TLCatalogTreeSchemaPageResponse,
  TLCatalogTreeTablePageResponse,
  TLHTTPValidationError,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Catalog Tree
 */
export const getCatalogTree = (
  params?: GetCatalogTreeParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCatalogTreeResponse>({ url: `/foundry/catalog-tree`, method: 'GET', params, signal }, options);
};

export const getGetCatalogTreeQueryKey = (params?: GetCatalogTreeParams) => {
  return ['foundry', 'catalog-tree', ...(params ? [params] : [])] as const;
};

export const getGetCatalogTreeQueryOptions = <
  TData = Awaited<ReturnType<typeof getCatalogTree>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogTreeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTree>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCatalogTreeQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCatalogTree>>> = ({ signal }) =>
    getCatalogTree(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCatalogTree>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCatalogTreeQueryResult = NonNullable<Awaited<ReturnType<typeof getCatalogTree>>>;
export type GetCatalogTreeQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCatalogTree<
  TData = Awaited<ReturnType<typeof getCatalogTree>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetCatalogTreeParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTree>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCatalogTree>>,
          TError,
          Awaited<ReturnType<typeof getCatalogTree>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCatalogTree<
  TData = Awaited<ReturnType<typeof getCatalogTree>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogTreeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTree>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCatalogTree>>,
          TError,
          Awaited<ReturnType<typeof getCatalogTree>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCatalogTree<
  TData = Awaited<ReturnType<typeof getCatalogTree>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogTreeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTree>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Catalog Tree
 */

export function useGetCatalogTree<
  TData = Awaited<ReturnType<typeof getCatalogTree>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogTreeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTree>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCatalogTreeQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Catalog Tree
 */
export const prefetchGetCatalogTreeQuery = async <
  TData = Awaited<ReturnType<typeof getCatalogTree>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetCatalogTreeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTree>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCatalogTreeQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Catalog Tree
 */
export const invalidateGetCatalogTree = async (
  queryClient: QueryClient,
  params?: GetCatalogTreeParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCatalogTreeQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary List Catalog Tree Catalogs
 */
export const listCatalogTreeCatalogs = (
  params?: ListCatalogTreeCatalogsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCatalogTreeCatalogPageResponse>(
    { url: `/foundry/catalog-tree/catalogs`, method: 'GET', params, signal },
    options,
  );
};

export const getListCatalogTreeCatalogsQueryKey = (params?: ListCatalogTreeCatalogsParams) => {
  return ['foundry', 'catalog-tree', 'catalogs', ...(params ? [params] : [])] as const;
};

export const getListCatalogTreeCatalogsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListCatalogTreeCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeCatalogs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCatalogTreeCatalogsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogTreeCatalogs>>> = ({ signal }) =>
    listCatalogTreeCatalogs(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListCatalogTreeCatalogsQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogTreeCatalogs>>>;
export type ListCatalogTreeCatalogsQueryError = ErrorType<TLHTTPValidationError>;

export function useListCatalogTreeCatalogs<
  TData = Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListCatalogTreeCatalogsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeCatalogs>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
          TError,
          Awaited<ReturnType<typeof listCatalogTreeCatalogs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCatalogTreeCatalogs<
  TData = Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListCatalogTreeCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeCatalogs>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
          TError,
          Awaited<ReturnType<typeof listCatalogTreeCatalogs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCatalogTreeCatalogs<
  TData = Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListCatalogTreeCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeCatalogs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Catalog Tree Catalogs
 */

export function useListCatalogTreeCatalogs<
  TData = Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListCatalogTreeCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeCatalogs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCatalogTreeCatalogsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Catalog Tree Catalogs
 */
export const prefetchListCatalogTreeCatalogsQuery = async <
  TData = Awaited<ReturnType<typeof listCatalogTreeCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListCatalogTreeCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeCatalogs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListCatalogTreeCatalogsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Catalog Tree Catalogs
 */
export const invalidateListCatalogTreeCatalogs = async (
  queryClient: QueryClient,
  params?: ListCatalogTreeCatalogsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListCatalogTreeCatalogsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary List Catalog Tree Schemas
 */
export const listCatalogTreeSchemas = (
  { catalog }: ListCatalogTreeSchemasPathParameters,
  params?: ListCatalogTreeSchemasParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCatalogTreeSchemaPageResponse>(
    { url: `/foundry/catalog-tree/catalogs/${catalog}/schemas`, method: 'GET', params, signal },
    options,
  );
};

export const getListCatalogTreeSchemasQueryKey = (
  { catalog }: ListCatalogTreeSchemasPathParameters,
  params?: ListCatalogTreeSchemasParams,
) => {
  return ['foundry', 'catalog-tree', 'catalogs', catalog, 'schemas', ...(params ? [params] : [])] as const;
};

export const getListCatalogTreeSchemasQueryOptions = <
  TData = Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { catalog }: ListCatalogTreeSchemasPathParameters,
  params?: ListCatalogTreeSchemasParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCatalogTreeSchemasQueryKey({ catalog }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogTreeSchemas>>> = ({ signal }) =>
    listCatalogTreeSchemas({ catalog }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: catalog !== null && catalog !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListCatalogTreeSchemasQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogTreeSchemas>>>;
export type ListCatalogTreeSchemasQueryError = ErrorType<TLHTTPValidationError>;

export function useListCatalogTreeSchemas<
  TData = Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogTreeSchemasPathParameters,
  params: undefined | ListCatalogTreeSchemasParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeSchemas>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
          TError,
          Awaited<ReturnType<typeof listCatalogTreeSchemas>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCatalogTreeSchemas<
  TData = Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogTreeSchemasPathParameters,
  params?: ListCatalogTreeSchemasParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeSchemas>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
          TError,
          Awaited<ReturnType<typeof listCatalogTreeSchemas>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCatalogTreeSchemas<
  TData = Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogTreeSchemasPathParameters,
  params?: ListCatalogTreeSchemasParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Catalog Tree Schemas
 */

export function useListCatalogTreeSchemas<
  TData = Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { catalog }: ListCatalogTreeSchemasPathParameters,
  params?: ListCatalogTreeSchemasParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCatalogTreeSchemasQueryOptions({ catalog }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Catalog Tree Schemas
 */
export const prefetchListCatalogTreeSchemasQuery = async <
  TData = Awaited<ReturnType<typeof listCatalogTreeSchemas>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { catalog }: ListCatalogTreeSchemasPathParameters,
  params?: ListCatalogTreeSchemasParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListCatalogTreeSchemasQueryOptions({ catalog }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Catalog Tree Schemas
 */
export const invalidateListCatalogTreeSchemas = async (
  queryClient: QueryClient,
  { catalog }: ListCatalogTreeSchemasPathParameters,
  params?: ListCatalogTreeSchemasParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListCatalogTreeSchemasQueryKey({ catalog }, params) }, options);

  return queryClient;
};

/**
 * @summary List Catalog Tree Tables
 */
export const listCatalogTreeTables = (
  { catalog }: ListCatalogTreeTablesPathParameters,
  params?: ListCatalogTreeTablesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCatalogTreeTablePageResponse>(
    { url: `/foundry/catalog-tree/catalogs/${catalog}/tables`, method: 'GET', params, signal },
    options,
  );
};

export const getListCatalogTreeTablesQueryKey = (
  { catalog }: ListCatalogTreeTablesPathParameters,
  params?: ListCatalogTreeTablesParams,
) => {
  return ['foundry', 'catalog-tree', 'catalogs', catalog, 'tables', ...(params ? [params] : [])] as const;
};

export const getListCatalogTreeTablesQueryOptions = <
  TData = Awaited<ReturnType<typeof listCatalogTreeTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { catalog }: ListCatalogTreeTablesPathParameters,
  params?: ListCatalogTreeTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCatalogTreeTablesQueryKey({ catalog }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogTreeTables>>> = ({ signal }) =>
    listCatalogTreeTables({ catalog }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: catalog !== null && catalog !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCatalogTreeTables>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListCatalogTreeTablesQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogTreeTables>>>;
export type ListCatalogTreeTablesQueryError = ErrorType<TLHTTPValidationError>;

export function useListCatalogTreeTables<
  TData = Awaited<ReturnType<typeof listCatalogTreeTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogTreeTablesPathParameters,
  params: undefined | ListCatalogTreeTablesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeTables>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCatalogTreeTables>>,
          TError,
          Awaited<ReturnType<typeof listCatalogTreeTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCatalogTreeTables<
  TData = Awaited<ReturnType<typeof listCatalogTreeTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogTreeTablesPathParameters,
  params?: ListCatalogTreeTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeTables>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCatalogTreeTables>>,
          TError,
          Awaited<ReturnType<typeof listCatalogTreeTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCatalogTreeTables<
  TData = Awaited<ReturnType<typeof listCatalogTreeTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogTreeTablesPathParameters,
  params?: ListCatalogTreeTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Catalog Tree Tables
 */

export function useListCatalogTreeTables<
  TData = Awaited<ReturnType<typeof listCatalogTreeTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { catalog }: ListCatalogTreeTablesPathParameters,
  params?: ListCatalogTreeTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCatalogTreeTablesQueryOptions({ catalog }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Catalog Tree Tables
 */
export const prefetchListCatalogTreeTablesQuery = async <
  TData = Awaited<ReturnType<typeof listCatalogTreeTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { catalog }: ListCatalogTreeTablesPathParameters,
  params?: ListCatalogTreeTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogTreeTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListCatalogTreeTablesQueryOptions({ catalog }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Catalog Tree Tables
 */
export const invalidateListCatalogTreeTables = async (
  queryClient: QueryClient,
  { catalog }: ListCatalogTreeTablesPathParameters,
  params?: ListCatalogTreeTablesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListCatalogTreeTablesQueryKey({ catalog }, params) }, options);

  return queryClient;
};
